import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector, shallowEqual } from "react-redux";
import PropTypes from "prop-types";

// Components
import SearchDropdown from "new_components/organisms/SearchDropdown";
import Table from "new_components/organisms/Table";
import ImageButton from "new_components/molecules/ImageButton";

// Constants
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
import Text from "new_components/atoms/Text";
import typography from "styles/typography";

// Providers
import { useUserSearchContext, UserSearchProvider } from "Providers/UserSearchProvider";

// Styles
const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  filter: ${({ isLoading }) => (isLoading ? "blur(4px)" : "none")};
  pointer-events: ${({ isLoading }) => (isLoading ? "none" : "auto")};
  transition: filter 0.3s ease, pointer-events 0.3s ease;
`;

const TableWrapper = styled.div`
  margin-top: 16px;
`;

const CollaboratorsSection = ({ formData, onUpdate }) => {
    const { get_employees } = useSelector(
        (state) => ({
            get_employees: state.employees,
        }),
        shallowEqual
    );

    const { userList } = useUserSearchContext();

    const [selectedCollaborators, setSelectedCollaborators] = useState(
        Array.isArray(formData.collaborators) ? formData.collaborators : []
    );

    const [loadingData, setLoadingData] = useState(true);

    // Function to add a collaborator if not already selected
    const handleAddCollaborator = (user) => {
        if (user.value !== -1 && !selectedCollaborators.some((u) => u.value === user.value)) {
            const updatedCollaborators = [...selectedCollaborators, user];
            setSelectedCollaborators(updatedCollaborators);
            onUpdate(updatedCollaborators);
        }
    };

    useEffect(() => {
        if (get_employees?.employees?.length > 0) {
            setLoadingData(false);
        }
    }, [get_employees]);

    useEffect(() => {
        if (formData.collaborators && formData.collaborators.length > 0) {
            setSelectedCollaborators(formData.collaborators);
        }
    }, [formData.collaborators]);

    if (loadingData) {
        return (
            <SectionWrapper isLoading={loadingData}>
                <p>Loading data, please wait...</p>
            </SectionWrapper>
        );
    }

    // Handle removing a collaborator
    const handleRemoveCollaborator = (userId) => {
        const updatedCollaborators = selectedCollaborators.filter((user) => user.value !== userId);
        setSelectedCollaborators(updatedCollaborators);
        onUpdate(updatedCollaborators);
    };

    return (
        <SectionWrapper isLoading={loadingData}>
            <Text
                color={colors.text.gray}
                typographyStyle={typography.body.semibold}
            >
                Add and manage collaborators on this pulse survey
            </Text>
            {/* Search Dropdown */}
            <SearchDropdown
                data={userList}
                onSearch={(item, searchTerm) =>
                    item.text.toLowerCase().includes(searchTerm.toLowerCase())
                }
                placeholder="Search Collaborators"
                onSelect={handleAddCollaborator}
            />

            {/* Table of Collaborators */}
            <TableWrapper>
                <Table
                    columns={[
                        { key: "user", label: "Collaborator" },
                        { key: "action", label: "Action" },
                    ]}
                    data={selectedCollaborators.map((user) => ({
                        user: user.text,
                        action: (
                            <ImageButton
                                text="Remove"
                                icon={ICON_SET.delete}
                                textStyle
                                color={colors.auxiliary.red[500]}
                                onClick={() => handleRemoveCollaborator(user.value)}
                            />
                        ),
                    }))}
                />

                {/* Display a message if no collaborators are assigned */}
                {selectedCollaborators.length === 0 && (
                    <Text
                        color={colors.text.gray}
                        typographyStyle={typography.body.semibold}
                    >
                        No collaborators assigned
                    </Text>
                )}
            </TableWrapper>
        </SectionWrapper>
    );
};

const CollaboratorsSectionWrapper = ({ formData, onUpdate }) => {
    return (
        <UserSearchProvider>
            <CollaboratorsSection formData={formData} onUpdate={onUpdate} />
        </UserSearchProvider>
    );
};

CollaboratorsSection.propTypes = {
    formData: PropTypes.shape({
        collaborators: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                text: PropTypes.string,
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })
        ),
        draft: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                text: PropTypes.string,
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })
        ),
    }),
    onUpdate: PropTypes.func.isRequired,
};

export default CollaboratorsSectionWrapper; 